/**
 * Agent Chat FAB — Floating Quick Chat Button
 * ═══════════════════════════════════════════════════════════════
 * Bottom-right floating button that opens a mini agent chat drawer.
 * Context-aware: suggests relevant agents based on current page route.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useCopilot } from '@components/copilot/CopilotProvider';
import AIMarkdown from './AIMarkdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useListAgentsQuery, useSendAgentMessageMutation } from '@api/zunoStudioApi';
import { Bot, X } from 'lucide-react';

const ROUTE_AGENT_MAP = {
  '/projects':  { category: 'project_management', prompt: 'What is the current project status?' },
  '/sprints':   { category: 'project_management', prompt: 'How is the current sprint going?' },
  '/budget':    { category: 'finance',             prompt: 'Show me the budget summary.' },
  '/accounts':  { category: 'finance',             prompt: 'Any overdue invoices?' },
  '/employees': { category: 'hr',                  prompt: 'Team overview please.' },
  '/goals':     { category: 'project_management', prompt: 'How are our OKRs tracking?' },
  '/timesheets':{ category: 'finance',             prompt: 'Time tracking summary this week.' },
  '/milestones':{ category: 'project_management', prompt: 'Any at-risk milestones?' },
  '/capacity':  { category: 'hr',                  prompt: 'Who is overloaded?' },
};

export default function AgentChatFAB() {
  // Provided by CopilotProvider. Null on routes with no copilot bound.
  const { boundAgent: copilotBound } = useCopilot();
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);

  const { data: agentsData } = useListAgentsQuery({ status: 'published', limit: 20 });
  const [sendMessage, { isLoading: sending }] = useSendAgentMessageMutation();
  const agents = agentsData?.data?.agents || [];

  // Context-aware suggestion
  const routeKey = Object.keys(ROUTE_AGENT_MAP).find(r => location.pathname.startsWith(r));
  const suggestion = routeKey ? ROUTE_AGENT_MAP[routeKey] : null;
  const suggestedAgent = suggestion ? agents.find(a => a.category === suggestion.category) : null;

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const handleSend = useCallback(async () => {
    const msg = input.trim();
    if (!msg || !selectedAgent || sending) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    try {
      const res = await sendMessage({ agentId: selectedAgent._id || selectedAgent.id, message: msg }).unwrap();
      setMessages(prev => [...prev, { role: 'assistant', content: res.data?.response || res.response || 'No response' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error — please try again.' }]);
    }
  }, [input, selectedAgent, sending, sendMessage]);

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setMessages([]);
    if (suggestion) setInput(suggestion.prompt);
  };

  // Two floating buttons in the same corner, looking nearly identical, is what
  // made the copilot appear to be "on every page". It was not — this general
  // launcher was, and the route-bound copilot orb sat on top of it where it
  // applied.
  //
  // One assistant at a time: when a copilot is bound to this route, it owns the
  // corner and this steps aside.
  if (agents.length === 0) return null;
  if (copilotBound) return null;

  return (
    <>
      {/* FAB Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 w-14 h-14 theme-gradient rounded-full shadow-lg flex items-center justify-center text-xl transition-all z-40 hover:scale-105"
        title="Chat with AI Agent"
        style={{ zIndex: 9990 }}
      >
        {open ? <X size={22} /> : <Bot size={24} />}
      </button>

      {/* Context suggestion chip */}
      {!open && suggestedAgent && (
        <button onClick={() => { setOpen(true); selectAgent(suggestedAgent); }}
          className="fixed bottom-6 right-24 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-md text-xs text-gray-600 hover:border-blue-300 transition-all z-40 animate-pulse"
          style={{ zIndex: 9989 }}>
          Ask {suggestedAgent.name}
        </button>
      )}

      {/* Chat Drawer */}
      {open && (
        <div className="fixed bottom-24 right-6 w-96 bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden"
          style={{ zIndex: 9991, maxHeight: '70vh' }}>
          {/* Header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
            {selectedAgent ? (
              <div className="flex items-center gap-2">
                <button onClick={() => { setSelectedAgent(null); setMessages([]); }} className="text-gray-400 hover:text-gray-600 text-xs">&larr;</button>
                <span className="text-sm font-semibold text-gray-700">{selectedAgent.name}</span>
              </div>
            ) : (
              <span className="text-sm font-semibold text-gray-700">Select an Agent</span>
            )}
            <button onClick={() => { if (selectedAgent) navigate(`/agent-space/${selectedAgent._id || selectedAgent.id}/chat`); }}
              className="text-[11px] text-blue-500 hover:text-blue-700">
              Full View
            </button>
          </div>

          {/* Agent picker or Chat */}
          {!selectedAgent ? (
            <div className="p-3 space-y-2 overflow-y-auto" style={{ maxHeight: '50vh' }}>
              {agents.map(a => (
                <button key={a._id || a.id} onClick={() => selectAgent(a)}
                  className={`w-full text-left p-3 border rounded-lg hover:border-blue-300 transition-colors ${suggestedAgent && (suggestedAgent._id || suggestedAgent.id) === (a._id || a.id) ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'}`}>
                  <div className="text-xs font-semibold text-gray-700">{a.name}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5 line-clamp-1">{a.description || 'No description'}</div>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: '200px', maxHeight: '40vh' }}>
                {messages.length === 0 && (
                  <div className="text-center py-6 text-xs text-gray-400">
                    Start chatting with {selectedAgent.name}
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs ${
                      msg.role === 'user' ? 'theme-gradient rounded-br-md' : 'bg-gray-100 text-gray-700 rounded-bl-md'
                    }`}>{msg.role === 'user' ? msg.content : <AIMarkdown content={msg.content} />}</div>
                  </div>
                ))}
                {sending && <div className="text-xs text-gray-400 pl-2">Thinking...</div>}
              </div>
              <div className="border-t border-gray-200 p-2 flex items-center gap-2 shrink-0">
                <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                  placeholder="Message..." className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-blue-400" />
                <button onClick={handleSend} disabled={!input.trim() || sending}
                  className="px-3 py-2 theme-gradient rounded-lg text-xs font-medium disabled:bg-gray-300 transition-colors">
                  Send
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
